<template>
  <div class="mt-4">
    <Tooltip
      :key="
        t('subscribeToEventUpdatesModal.firstStep.form.dateType.toolTipText')
      "
      :tooltip-text="
        t('subscribeToEventUpdatesModal.firstStep.form.dateType.toolTipText')
      "
      :disabled="!tooltipEnabled"
      length="large"
      type="span"
      class="self-center mb-2 block"
    >
      <label for="date-type-select" class="block mb-2 required">
        {{ t('subscribeToEventUpdatesModal.firstStep.form.dateType.label') }}
      </label>
    </Tooltip>
    <FormSelect
      id="date-type-select"
      v-model="dateType"
      :error="dateTypeError"
      data-test-id="date-type-select"
    >
      <option value="" disabled hidden>
        {{
          t('subscribeToEventUpdatesModal.firstStep.form.dateType.placeholder')
        }}
      </option>
      <option v-if="publicAllowed" value="PUBLIC">
        {{ t('subscribeToEventUpdatesModal.firstStep.form.dateType.public') }}
      </option>
      <option value="PRIVATE">
        {{ t('subscribeToEventUpdatesModal.firstStep.form.dateType.private') }}
      </option>
    </FormSelect>
  </div>
</template>

<script setup lang="ts">
import FormSelect from '~/components/common/form/FormSelect.vue'
import type {
  DateRequestErrors,
  DateRequestInvalidKeys,
} from '~/components/date-request/dateRequestTypes'
import type { EventDateType } from '~/composables/types/event'

interface Props {
  tooltipEnabled?: boolean
  errors?: DateRequestErrors | null
  invalidKeys?: DateRequestInvalidKeys
  publicAllowed?: boolean
}

const {
  tooltipEnabled = false,
  errors = {},
  invalidKeys = [],
  publicAllowed = true,
} = defineProps<Props>()

const modelValue = defineModel<EventDateType | null>({ required: true })

const emit = defineEmits<{
  (e: 'clear-errors', field: string): void
}>()

const { t } = useI18n()

// Private is the only option when public is disabled, so preselect it.
watchEffect(() => {
  if (!publicAllowed && modelValue.value !== 'PRIVATE') {
    modelValue.value = 'PRIVATE'
  }
})

const dateTypeError = computed(() =>
  invalidKeys.includes('dateType')
    ? errors?.dateType || t('general.form.validation.required')
    : undefined
)

const dateType = computed<EventDateType | ''>({
  get: () => modelValue.value ?? '',
  set: (value) => {
    modelValue.value = value === '' ? null : (value as EventDateType)
    emit('clear-errors', 'dateType')
  },
})
</script>

<style lang="postcss" scoped>
.required:after {
  content: '*';
  position: relative;
  font-size: inherit;
  @apply text-red;
  padding-left: 0.25rem;
  font-weight: 700;
}
</style>
